/**
 * forecast/scratchpad.ts — quick-261001-nym "Exec Scratchpad".
 *
 * John, 2026-10-01: "have a prompt where someone can input some type of
 * scenario, like 'if we only close trustmark, alight, and the unum managed
 * services, and then like 20% of others, what is the total?' so that you can
 * have freeform interaction and mathematical output that is less work than an
 * exec having to tally them up outside on a piece of paper."
 *
 * DETERMINISTIC, NOT A MODEL. The sentence is read with three rules, and the
 * answer always shows how it was read, so a mis-read is visible rather than
 * silently wrong:
 *   1. Deals are picked by name: a word of 4+ letters in the sentence that
 *      starts a word of the deal's account or opportunity name picks that deal
 *      (so "trustmark" picks "Trustmark- LIMRA…", "alight" picks both Alight
 *      deals unless "managed" / "product" right after the name narrows it).
 *   2. "N%" sets the rate for every OTHER open deal. "only" with no percentage
 *      means the others count for nothing.
 *   3. Picked deals and Closed Won count at 100%.
 * Browser-safe; the CEO artifact carries the same code verbatim.
 */
import type { ForecastDeal } from './schemas.js';

export interface ScratchpadLine {
  kind: 'won' | 'picked' | 'others' | 'note';
  text: string;
  amount: number;
}

export interface ScratchpadResult {
  /** Deals read as closing (100%). */
  picked: ForecastDeal[];
  /** Rate applied to every other open deal, 0..100; null when the sentence set none. */
  othersPct: number | null;
  wonTotal: number;
  pickedTotal: number;
  othersTotal: number;
  total: number;
  lines: ScratchpadLine[];
  /** True when nothing in the sentence picked a deal or set a rate. */
  empty: boolean;
}

const STOP = new Set([
  'the', 'and', 'then', 'like', 'only', 'just', 'close', 'closing', 'closed', 'win', 'what', 'total', 'others',
  'other', 'rest', 'everything', 'else', 'with', 'from', 'that', 'this', 'deals', 'deal', 'percent', 'plus',
  'managed', 'services', 'product', 'about', 'around', 'roughly', 'assume', 'assuming', 'also', 'both', 'all',
  'quarter', 'forecast', 'arr', 'does', 'would', 'will', 'should', 'into', 'half',
]);

function words(s: string): string[] {
  return s.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 0);
}

function nameWords(d: ForecastDeal): string[] {
  return words(d.name);
}

const isWon = (d: ForecastDeal): boolean => d.tier === 'closed_won' || d.forecast_position === 'Closed Won';
const isManaged = (d: ForecastDeal): boolean => d.sub_group === 'Managed Services';

/** Read a scenario sentence against one quarter's deals. */
export function readScenario(text: string, deals: readonly ForecastDeal[]): ScratchpadResult {
  const toks = words(text);
  const open = deals.filter((d) => !isWon(d));
  const won = deals.filter(isWon);

  // Rule 2 — the "others" rate.
  const pctMatch = /(\d{1,3}(?:\.\d+)?)\s*%/.exec(text) ?? /(\d{1,3}(?:\.\d+)?)\s*(?:percent|pct)\b/i.exec(text);
  const saysOnly = toks.includes('only') || toks.includes('just');
  const saysNothingElse = /nothing else|none of the (?:others|rest)|no others/i.test(text);
  let othersPct: number | null = pctMatch ? Math.max(0, Math.min(100, Number(pctMatch[1]))) : null;
  if (othersPct === null && (saysOnly || saysNothingElse)) othersPct = 0;

  // Rule 1 — picked deals.
  const picked = new Set<ForecastDeal>();
  const ambiguous: string[] = [];
  toks.forEach((tok, i) => {
    if (tok.length < 4 || STOP.has(tok) || /^\d+$/.test(tok)) return;
    const hits = open.filter((d) => nameWords(d).some((w) => w.startsWith(tok)));
    if (hits.length === 0) return;
    const after = toks.slice(i + 1, i + 4);
    const wantsManaged = after.includes('managed') || after.includes('services');
    const wantsProduct = after.includes('product') || after.includes('platform');
    let chosen = hits;
    if (wantsManaged && hits.some(isManaged)) chosen = hits.filter(isManaged);
    else if (wantsProduct && hits.some((d) => !isManaged(d))) chosen = hits.filter((d) => !isManaged(d));
    if (chosen.length > 1 && !wantsManaged && !wantsProduct) ambiguous.push(tok);
    chosen.forEach((d) => picked.add(d));
  });

  const pickedList = [...picked];
  const others = open.filter((d) => !picked.has(d));
  const wonTotal = won.reduce((t, d) => t + d.raw_amount, 0);
  const pickedTotal = pickedList.reduce((t, d) => t + d.raw_amount, 0);
  const rate = othersPct === null ? 0 : othersPct / 100;
  const othersTotal = others.reduce((t, d) => t + d.raw_amount * rate, 0);
  const total = wonTotal + pickedTotal + othersTotal;

  const money = (n: number): string => '$' + Math.round(n).toLocaleString('en-US');
  const lines: ScratchpadLine[] = [];
  if (won.length) lines.push({ kind: 'won', amount: wonTotal, text: `Closed Won (${String(won.length)}) at 100%: ${money(wonTotal)}` });
  pickedList.forEach((d) => lines.push({ kind: 'picked', amount: d.raw_amount, text: `${d.name}${isManaged(d) ? ' (Managed Services)' : ''} at 100%: ${money(d.raw_amount)}` }));
  if (others.length) {
    lines.push({
      kind: 'others',
      amount: othersTotal,
      text: othersPct === null
        ? `${String(others.length)} other open deal${others.length === 1 ? '' : 's'} (${money(others.reduce((t, d) => t + d.raw_amount, 0))} face) — no rate given, counted at 0%`
        : `${String(others.length)} other open deal${others.length === 1 ? '' : 's'} at ${String(othersPct)}%: ${money(othersTotal)}`,
    });
  }
  ambiguous.forEach((tok) => lines.push({ kind: 'note', amount: 0, text: `"${tok}" matched more than one deal — all of them were counted; add "managed" or "product" after the name to narrow it.` }));

  const empty = pickedList.length === 0 && othersPct === null;
  return { picked: pickedList, othersPct, wonTotal, pickedTotal, othersTotal, total, lines, empty };
}

// ---- quick-261001-nym (round 2) — Claude-assisted reading ------------------
//
// John, 2026-10-01: "lets make it more sophisticated". Claude is asked ONLY to
// interpret the sentence — which deals close, what rate the rest get, what it
// assumed — as JSON; the arithmetic stays here and the working is still shown.
// Both surfaces use this same prompt and the same `applyScenario`.

/** A compact, model-facing view of one deal (no amounts hidden; the model may reason on size). */
export interface ScenarioDealBrief {
  id: string;
  name: string;
  stream: 'product' | 'managed';
  position: string;
  amount: number;
  won: boolean;
}

export function briefDeals(deals: readonly ForecastDeal[]): ScenarioDealBrief[] {
  return deals.map((d) => ({
    id: d.opp_id,
    name: d.name,
    stream: isManaged(d) ? 'managed' : 'product',
    position: isWon(d) ? 'Closed Won' : d.forecast_position,
    amount: d.raw_amount,
    won: isWon(d),
  }));
}

/** What Claude is asked to return. */
export interface ScenarioReading {
  picked: string[];
  othersPct: number | null;
  assumptions: string[];
}

export const SCENARIO_SYSTEM_PROMPT =
  'You turn a sales executive\'s scenario sentence into a structured reading over a list of deals for one quarter. ' +
  'Reply with ONLY a JSON object, no prose: {"picked": [deal ids that the sentence says will close, counted at 100%], ' +
  '"othersPct": a number 0-100 for the rate applied to EVERY OTHER open deal, or null if the sentence gives none, ' +
  '"assumptions": [short plain-English notes on how you read ambiguous parts]}. ' +
  'Rules: match deals by account or deal name, tolerating misspellings and shorthand; "the X managed services" means X\'s managed-services deal; ' +
  '"only"/"just" with no percentage means othersPct = 0; "half" means 50, "a third" means 33; Closed Won deals always count and need not be listed; ' +
  'never invent deals or ids; if a name matches several deals and the sentence does not narrow it, pick them all and say so in assumptions.';

export function scenarioUserPrompt(text: string, briefs: readonly ScenarioDealBrief[]): string {
  const rows = briefs.map((b) => `${b.id} | ${b.name} | ${b.stream} | ${b.position} | ${String(b.amount)}`).join('\n');
  return `Deals (id | name | stream | position | amount):\n${rows}\n\nSentence: "${text}"`;
}

/** Tolerant parse of the model's reply (a bare object, or an object inside prose). */
export function parseScenarioReading(raw: unknown): ScenarioReading | null {
  let obj: unknown = raw;
  if (typeof raw === 'string') {
    const m = /\{[\s\S]*\}/.exec(raw);
    if (!m) return null;
    try { obj = JSON.parse(m[0]); } catch { return null; }
  }
  if (!obj || typeof obj !== 'object') return null;
  const o = obj as Record<string, unknown>;
  const picked = Array.isArray(o.picked) ? o.picked.filter((x): x is string => typeof x === 'string') : [];
  const pct = o.othersPct;
  const othersPct = typeof pct === 'number' && Number.isFinite(pct) ? Math.max(0, Math.min(100, pct)) : null;
  const assumptions = Array.isArray(o.assumptions) ? o.assumptions.filter((x): x is string => typeof x === 'string') : [];
  return { picked, othersPct, assumptions };
}

/** The arithmetic for a reading — the same lines `readScenario` produces, plus the model's assumptions as notes. */
export function applyScenario(deals: readonly ForecastDeal[], reading: ScenarioReading): ScratchpadResult {
  const won = deals.filter(isWon);
  const open = deals.filter((d) => !isWon(d));
  const pickedSet = new Set(reading.picked);
  const pickedList = open.filter((d) => pickedSet.has(d.opp_id));
  const others = open.filter((d) => !pickedSet.has(d.opp_id));
  const othersPct = reading.othersPct;
  const wonTotal = won.reduce((t, d) => t + d.raw_amount, 0);
  const pickedTotal = pickedList.reduce((t, d) => t + d.raw_amount, 0);
  const rate = othersPct === null ? 0 : othersPct / 100;
  const othersTotal = others.reduce((t, d) => t + d.raw_amount * rate, 0);
  const money = (n: number): string => '$' + Math.round(n).toLocaleString('en-US');
  const lines: ScratchpadLine[] = [];
  if (won.length) lines.push({ kind: 'won', amount: wonTotal, text: `Closed Won (${String(won.length)}) at 100%: ${money(wonTotal)}` });
  pickedList.forEach((d) => lines.push({ kind: 'picked', amount: d.raw_amount, text: `${d.name}${isManaged(d) ? ' (Managed Services)' : ''} at 100%: ${money(d.raw_amount)}` }));
  if (others.length) {
    lines.push({
      kind: 'others', amount: othersTotal,
      text: othersPct === null
        ? `${String(others.length)} other open deal${others.length === 1 ? '' : 's'} (${money(others.reduce((t, d) => t + d.raw_amount, 0))} face) — no rate given, counted at 0%`
        : `${String(others.length)} other open deal${others.length === 1 ? '' : 's'} at ${String(othersPct)}%: ${money(othersTotal)}`,
    });
  }
  reading.assumptions.forEach((a) => lines.push({ kind: 'note', amount: 0, text: a }));
  return {
    picked: pickedList, othersPct, wonTotal, pickedTotal, othersTotal,
    total: wonTotal + pickedTotal + othersTotal, lines,
    empty: pickedList.length === 0 && othersPct === null,
  };
}
